import { useQuery } from '@apollo/client';
import React, { ReactNode } from 'react';
import { Redirect, useLocation } from 'react-router-dom';
import { AliasResolution, aliasRouteFromPath, canonicalPublicPath } from './canonicalSlugRedirectUtils';
import { CANONICAL_ROUTE_SLUG_CHECK, SLUG_ALIAS_RESOLUTION } from './slugAliasResolutionQuery';

const CanonicalSlugRedirect = ({ children }: { children: ReactNode }): React.ReactElement => {
  const location = useLocation();
  const aliasRoute = aliasRouteFromPath(location.pathname);
  const { data, loading } = useQuery<AliasResolution>(SLUG_ALIAS_RESOLUTION, {
    variables: { slug: aliasRoute?.slug },
    skip: !aliasRoute,
    fetchPolicy: 'network-only',
  });
  const isPublicQuest = aliasRoute?.kind === 'quest'
    && (!aliasRoute.rest || /^\/+$/u.test(aliasRoute.rest));
  const { data: canonicalQuestData } = useQuery(CANONICAL_ROUTE_SLUG_CHECK, {
    variables: { slug: aliasRoute?.slug },
    skip: !isPublicQuest,
    fetchPolicy: 'network-only',
  });

  const canonicalPath = aliasRoute ? canonicalPublicPath(aliasRoute, data) : null;
  const target = canonicalPath && aliasRoute ? `${canonicalPath}${aliasRoute.rest}` : null;
  const isDashboardAlias = Boolean(aliasRoute?.rest && /\/dashboard\/?$/.test(aliasRoute.rest));
  const unresolvedPublicQuestAlias = isPublicQuest && !canonicalQuestData?.routeBySlug;

  // These pages query canonical slugs and redirect missing results to /error.
  // Resolve aliases first; keep editors and play routes mounted during lookup.
  if (loading && (isDashboardAlias || unresolvedPublicQuestAlias)) {
    return <div className="loading" />;
  }

  if (target && target !== location.pathname) {
    return <Redirect to={{ pathname: target, search: location.search, hash: location.hash }} />;
  }

  return <>{children}</>;
};

export default CanonicalSlugRedirect;
